/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { 
  Search, 
  Wrench,
  ChevronRight,
  ExternalLink,
  Loader2,
  X,
  Plug,
  File,
} from 'lucide-react';

// Services
import {
  // R10: Use index query key and function for cache coordination
  SKILLS_QUERY_KEYS,
  skillsIndexQueryFn,
  searchSkills,
  readSkill,
  listSkillFiles,
  readSkillFile,
  // User settings
  getUserSettings,
  updatePlaybooksSettings,
} from '@/services/skillshop';
import type { UISettings } from '@shared/types/user_settings';
import type { SkillEntry } from '@/services/skillshop';
import type { SkillReadResult } from '@shared/schema/skills';
import { InstallButton, InstallButtonCompact } from './InstallButton';

export function SkillsTab() {
  // R9: Persist filter state in URL for shareability and navigation survival
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Read filters from URL with defaults
  const typeFilter = (searchParams.get('type') as 'all' | 'procedural' | 'connector') || 'all';
  const categoryFilter = searchParams.get('category') || 'all';
  const selectedSkillPath = searchParams.get('path');
  
  // Local state for search query (not persisted until search executed)
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  
  const queryClient = useQueryClient();

  // Fetch user settings to restore saved preferences
  const { data: userSettings } = useQuery({
    queryKey: ['user-settings'],
    queryFn: getUserSettings,
    staleTime: 5 * 60 * 1000,
  });

  // Initialize settings from user preferences once loaded
  useEffect(() => {
    if (userSettings && !settingsLoaded) {
      const playbooksSettings = (userSettings.uiSettings as UISettings)?.playbooksSettings;
      if (playbooksSettings) {
        // Restore type filter if not in URL
        if (!searchParams.get('type') && playbooksSettings.defaultSkillsTypeFilter) {
          setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (playbooksSettings.defaultSkillsTypeFilter !== 'all') {
              next.set('type', playbooksSettings.defaultSkillsTypeFilter!);
            }
            return next;
          });
        }
      }
      setSettingsLoaded(true);
    }
  }, [userSettings, settingsLoaded, searchParams, setSearchParams]);
  
  // R9: Helper to update URL params while preserving others
  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === 'all' || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }
      return next;
    });
  }, [setSearchParams]);

  // Update type filter and persist to user settings
  const handleTypeChange = useCallback((type: 'all' | 'procedural' | 'connector') => {
    updateParams({ type });
    updatePlaybooksSettings({ defaultSkillsTypeFilter: type });
  }, [updateParams]);

  // R10: Fetch skills via index endpoint - shares cache with parent component
  // This avoids redundant API calls since Playbooks.tsx also fetches the index
  const { data: skillsIndex, isLoading: isLoadingSkills } = useQuery({
    queryKey: SKILLS_QUERY_KEYS.index,
    queryFn: skillsIndexQueryFn,
    staleTime: 5 * 60 * 1000,
  });
  const skills = skillsIndex?.entries;

  // Search skills
  const { data: searchResults, isLoading: isSearching, refetch: doSearch } = useQuery({
    queryKey: ['skills-search', searchQuery, typeFilter, categoryFilter],
    queryFn: async () => {
      if (!searchQuery.trim()) return null;
      
      return searchSkills(searchQuery, {
        type: typeFilter !== 'all' ? typeFilter : undefined,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        limit: 50,
      });
    },
    enabled: false, // Manual trigger
  });

  // R6: React Query for skill content - caches and handles loading states
  const { 
    data: skillContent, 
    isLoading: isLoadingContent,
  } = useQuery({
    queryKey: ['skill-content', selectedSkillPath],
    queryFn: () => readSkill(selectedSkillPath!),
    enabled: !!selectedSkillPath,
    staleTime: 5 * 60 * 1000, // Cache content for 5 minutes
  });

  // R6: React Query for skill files list
  const { 
    data: skillFiles = [],
  } = useQuery({
    queryKey: ['skill-files', selectedSkillPath],
    queryFn: () => listSkillFiles(selectedSkillPath!).catch(() => []),
    enabled: !!selectedSkillPath,
    staleTime: 5 * 60 * 1000,
  });

  // Get selected skill metadata from list
  const selectedSkill = selectedSkillPath
    ? skills?.find(s => s.path === selectedSkillPath) ?? 
      searchResults?.results?.find(r => r.entry.path === selectedSkillPath)?.entry ?? 
      null
    : null;

  // R6 + R9: Select skill by path - React Query handles fetching, URL persists selection
  const selectSkill = useCallback((skill: SkillEntry) => {
    updateParams({ path: skill.path });
  }, [updateParams]);

  // R6 + R9: Close focus view - React Query keeps data cached
  const closeFocusView = useCallback(() => {
    updateParams({ path: null });
  }, [updateParams]);

  // R9: Handle search and persist query to URL
  const handleSearch = useCallback(() => {
    if (searchQuery.trim()) {
      updateParams({ q: searchQuery.trim() });
      doSearch();
    }
  }, [searchQuery, doSearch, updateParams]);

  // R9: Clear search and remove from URL
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    updateParams({ q: null });
    queryClient.removeQueries({ queryKey: ['skills-search'] });
  }, [queryClient, updateParams]);

  // Get unique categories from skills
  const categories = skills 
    ? [...new Set(skills.map(s => s.category).filter((c): c is NonNullable<typeof c> => Boolean(c)))]
    : [];

  // Determine which skills to display
  const displaySkills = searchResults?.results 
    ? searchResults.results.map(r => r.entry)
    : skills;

  // Filter skills based on type and category
  const filteredSkills = displaySkills?.filter(s => {
    if (typeFilter !== 'all' && s.type !== typeFilter) return false;
    if (categoryFilter !== 'all' && s.category !== categoryFilter) return false;
    return true;
  });

  return (
    <div className="flex h-[calc(100vh-320px)]">
      {/* Skills List */}
      <div className={`flex flex-col ${selectedSkillPath ? 'w-1/2 border-r' : 'w-full'}`}>
        {/* Search and Filters */}
        <div className="p-4 border-b space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search skills..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-9"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button onClick={handleSearch} disabled={!searchQuery.trim() || isSearching}>
              {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
            </Button>
          </div>
          
          <div className="flex items-center gap-4">
            {/* R9: Type selector buttons persisted to URL and user settings */}
            <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
              <Button
                variant="ghost"
                size="sm"
                className={`px-3 ${typeFilter === 'all' ? 'bg-white shadow-sm text-primary font-medium hover:bg-white' : 'hover:bg-muted-foreground/10'}`}
                onClick={() => handleTypeChange('all')}
              >
                All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`px-3 ${typeFilter === 'procedural' ? 'bg-white shadow-sm text-primary font-medium hover:bg-white' : 'hover:bg-muted-foreground/10'}`}
                onClick={() => handleTypeChange('procedural')}
              >
                Procedural
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`px-3 ${typeFilter === 'connector' ? 'bg-white shadow-sm text-primary font-medium hover:bg-white' : 'hover:bg-muted-foreground/10'}`}
                onClick={() => handleTypeChange('connector')}
              >
                Connector
              </Button>
            </div>
            <Select value={categoryFilter} onValueChange={(v) => updateParams({ category: v })}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(category => (
                  <SelectItem key={category} value={category}>{category}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Skills Cards */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {isLoadingSkills ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredSkills?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <Wrench className="h-12 w-12 mb-4 opacity-50" />
              <p>No skills found</p>
              {searchQuery && (
                <Button variant="link" onClick={clearSearch}>Clear search</Button>
              )}
            </div>
          ) : (
            filteredSkills?.map((skill) => (
              <SkillCard
                key={skill.path}
                skill={skill}
                isSelected={selectedSkillPath === skill.path}
                onClick={() => selectSkill(skill)}
              />
            ))
          )}
        </div>
      </div>

      {/* Focus View (Skill Detail) - R6: React Query handles loading/caching */}
      {selectedSkillPath && selectedSkill && (
        <SkillFocusView
          skill={selectedSkill}
          content={skillContent ?? null}
          files={skillFiles}
          isLoadingContent={isLoadingContent}
          onClose={closeFocusView}
        />
      )}
    </div>
  );
}

// Skill Card Component
interface SkillCardProps {
  skill: SkillEntry;
  isSelected: boolean;
  onClick: () => void;
}

function SkillCard({ skill, isSelected, onClick }: SkillCardProps) {
  return (
    <div
      className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
        isSelected ? 'border-primary bg-primary/5' : 'bg-slate-50 hover:border-gray-300'
      }`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            {skill.type === 'connector' ? (
              <Plug className="h-4 w-4 text-purple-500 flex-shrink-0" />
            ) : (
              <Wrench className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            )}
            <h3 className="font-medium truncate">{skill.name}</h3>
          </div>
          {skill.description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
              {skill.description}
            </p>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={skill.type === 'connector' ? 'secondary' : 'outline'}>
              {skill.type}
            </Badge>
            {skill.category && (
              <Badge variant="outline" className="bg-gray-50">{skill.category}</Badge>
            )}
            {skill.hasScripts && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <File className="h-3 w-3 mr-1" />
                Scripts
              </Badge>
            )}
            {skill.hasTemplates && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                Templates
              </Badge>
            )}
            {skill.mcpServer && (
              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                MCP: {skill.mcpServer}
              </Badge>
            )}
          </div>
          {skill.tags && skill.tags.length > 0 && (
            <div className="flex gap-1 flex-wrap mt-2">
              {skill.tags.slice(0, 5).map((tag, idx) => (
                <span key={idx} className="text-xs text-muted-foreground bg-gray-100 px-2 py-0.5 rounded">
                  {tag}
                </span>
              ))}
              {skill.tags.length > 5 && (
                <span className="text-xs text-muted-foreground">+{skill.tags.length - 5} more</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
          <InstallButtonCompact
            type="skill"
            itemId={skill.path}
            itemName={skill.name}
          />
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </div>
      </div>
    </div>
  );
}

// Skill Focus View Component
interface SkillFocusViewProps {
  skill: SkillEntry;
  content: SkillReadResult | null;
  files: string[];
  isLoadingContent: boolean;
  onClose: () => void;
}

function SkillFocusView({ skill, content, files, isLoadingContent, onClose }: SkillFocusViewProps) {
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const { toast } = useToast();

  const loadFile = async (filePath: string) => {
    setSelectedFile(filePath);
    setLoadingFile(true);
    try {
      const content = await readSkillFile(skill.path, filePath);
      setFileContent(content);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load file content.',
        variant: 'destructive',
      });
    } finally {
      setLoadingFile(false);
    }
  };

  return (
    <div className="w-1/2 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {skill.type === 'connector' ? (
              <Plug className="h-5 w-5 text-purple-500" />
            ) : (
              <Wrench className="h-5 w-5 text-muted-foreground" />
            )}
            <h2 className="text-lg font-semibold truncate">{skill.name}</h2>
          </div>
          <p className="text-sm text-muted-foreground truncate">{skill.path}</p>
        </div>
        <div className="flex items-center gap-2 ml-4">
          <InstallButton
            type="skill"
            itemId={skill.path}
            itemName={skill.name}
          />
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoadingContent ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : content ? (
          <div className="space-y-6">
            {/* Description */}
            {skill.description && (
              <div className="bg-gray-50 border rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Description</h4>
                <p className="text-sm">{skill.description}</p>
              </div>
            )}

            {/* SKILL.md Content */}
            {content.content && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">SKILL.md</h4>
                <div className="bg-gray-50 rounded-lg p-4 text-sm font-mono whitespace-pre-wrap overflow-x-auto max-h-[300px]">
                  {content.content}
                </div>
              </div>
            )}

            {/* Files */}
            {files.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Files</h4>
                <div className="space-y-2">
                  {files.map((file) => (
                    <div
                      key={file}
                      className={`flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50 ${
                        selectedFile === file ? 'border-primary bg-primary/5' : ''
                      }`}
                      onClick={() => loadFile(file)}
                    >
                      <File className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm truncate">{file}</span>
                    </div>
                  ))}
                </div>

                {/* File Content */}
                {selectedFile && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-sm font-medium">{selectedFile}</h5>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedFile(null);
                          setFileContent(null);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {loadingFile ? (
                      <div className="flex items-center justify-center p-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <div className="bg-gray-900 text-gray-100 rounded-lg p-4 text-sm font-mono whitespace-pre-wrap overflow-x-auto max-h-[300px]">
                        {fileContent}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Metadata */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Details</h4>
              <dl className="text-sm space-y-2">
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-24">Type:</dt>
                  <dd>
                    <Badge variant={skill.type === 'connector' ? 'secondary' : 'outline'}>
                      {skill.type}
                    </Badge>
                  </dd>
                </div>
                {skill.category && (
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground w-24">Category:</dt>
                    <dd>{skill.category}</dd>
                  </div>
                )}
                {skill.version && (
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground w-24">Version:</dt>
                    <dd>{skill.version}</dd>
                  </div>
                )}
                {skill.mcpServer && (
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground w-24">MCP Server:</dt>
                    <dd>{skill.mcpServer}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Tags */}
            {skill.tags && skill.tags.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Tags</h4>
                <div className="flex gap-1 flex-wrap">
                  {skill.tags.map((tag, idx) => (
                    <span key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
